import type { Message } from '@chatapp-inbox/shared';

/** Depois deste tempo "Enviando…" passa a "Ainda enviando…" (a pessoa sabe que está demorando). */
export const STALE_PENDING_MS = 30_000;

export const SENDING_TEXT = 'Enviando…';
export const STILL_SENDING_TEXT = 'Ainda enviando…';
export const UNCONFIRMED_TEXT = 'Não foi possível confirmar o envio.';

/** Aviso quando o serviço de mensagens (o que entrega ao WhatsApp) não responde: a interface não o nomeia. */
export const PROVIDER_UNAVAILABLE_TEXT =
  'O serviço de mensagens está indisponível no momento. As mensagens não foram enviadas; tente reenviar mais tarde.';

/**
 * Motivo legível de uma falha de envio, pelo `errorCode` do servidor. `unsure` marca o caso em que
 * a mensagem PODE ter chegado: reenviar sem conferir pode duplicá-la para o cliente.
 */
export function failureReason(errorCode: string | null): { text: string; unsure: boolean } {
  switch (errorCode) {
    case 'max_retries':
      return { text: 'O WhatsApp não respondeu. Tente reenviar.', unsure: false };
    case 'unknown_delivery':
      return {
        text: 'Não sabemos se foi entregue. Confira no WhatsApp antes de reenviar para não duplicar.',
        unsure: true,
      };
    case 'rejected':
      return { text: 'O WhatsApp recusou esta mensagem.', unsure: false };
    case 'internal_error':
      return { text: 'Erro interno ao enviar.', unsure: false };
    // Número desligado/removido ou conexão sem configuração: reenviar só adianta depois de um
    // administrador resolver, e o texto diz quem precisa agir.
    case 'line_disabled':
      return {
        text: 'O número desta conversa está desligado. Peça a um administrador para ligá-lo e reenvie.',
        unsure: false,
      };
    case 'line_not_found':
      return {
        text: 'O número desta conversa não existe mais na conexão. Fale com um administrador.',
        unsure: false,
      };
    case 'not_connected':
      return {
        text: 'A conexão do WhatsApp não está configurada. Peça a um administrador para configurá-la.',
        unsure: false,
      };
    default:
      return { text: 'Falha no envio.', unsure: false };
  }
}

/**
 * O que fazer, em português, para os códigos de recusa da Meta que mais aparecem. Vale só para quem tem o código no motivo
 * guardado ("Código 131042: …"); o resto fica só com o texto que a própria Meta deu. Não promete o que a Meta não confirmou:
 * diz onde olhar.
 */
const META_HINTS: Record<number, string> = {
  131042:
    'A conta do WhatsApp Business ainda não tem a moeda e a forma de pagamento configuradas na Meta (modelos de mensagem são cobrados). Quem administra a conta deve configurá-las no Gerenciador do WhatsApp, em Pagamentos (ou Faturamento); depois, reenvie.',
  131049:
    'A Meta segurou a mensagem para proteger a qualidade da conta (limite de modelos para este cliente). Espere o cliente responder ou tente mais tarde.',
  131047:
    'Passaram mais de 24 horas desde a última mensagem do cliente: só um modelo de mensagem aprovado pode ser enviado.',
  131026:
    'A Meta não conseguiu entregar: o número do cliente pode não ter WhatsApp, ter bloqueado o seu número ou ainda não ter aceitado os termos do WhatsApp.',
  131030:
    'O número do cliente não está na lista permitida desta conta (contas de teste só enviam a números cadastrados).',
  131056: 'Muitas mensagens para o mesmo cliente em pouco tempo. Espere um pouco e reenvie.',
  132000:
    'As variáveis enviadas não batem com o modelo cadastrado na Meta. Confira se todas foram preenchidas.',
  132012:
    'O formato de uma variável não confere com o modelo cadastrado na Meta. Confira os valores e o modelo.',
  132015:
    'O modelo está pausado pela Meta por baixa qualidade. Use outro modelo ou revise este no Gerenciador do WhatsApp.',
  132016:
    'O modelo foi desativado pela Meta por baixa qualidade. Use outro modelo ou crie um novo no Gerenciador do WhatsApp.',
  132001:
    'O modelo não existe neste idioma na conta do WhatsApp. Confira o nome e o idioma no Gerenciador do WhatsApp.',
};

/** A orientação para o `errorDetail` guardado ("Código 131042: …"), ou `null` se não há uma para aquele código. */
export function metaHint(detail: string | null | undefined): string | null {
  const code = /^Código (\d{1,7})(?:\/\d+)?(?::|$)/.exec(detail ?? '')?.[1];
  return code === undefined ? null : (META_HINTS[Number(code)] ?? null);
}

/** Rótulo do botão de reenvio: na entrega incerta o texto lembra do risco de duplicar. */
export function retryLabel(errorCode: string | null): string {
  return failureReason(errorCode).unsure ? 'Reenviar mesmo assim' : 'Reenviar';
}

/** A mensagem está "enviando" há mais de {@link STALE_PENDING_MS} (a partir de `since`, em ms)? */
export function isStale(since: number, now: number): boolean {
  return now - since > STALE_PENDING_MS;
}

/**
 * Texto do estado de uma mensagem de saída do servidor. `stale` só vale para `pending`. Mensagens
 * de entrada não têm estado a mostrar.
 */
export function statusText(message: Pick<Message, 'status' | 'errorCode'>, stale = false): string {
  switch (message.status) {
    case 'pending':
      return stale ? STILL_SENDING_TEXT : SENDING_TEXT;
    case 'sent':
      return 'Enviada';
    case 'delivered':
      return 'Entregue';
    case 'read':
      return 'Lida';
    case 'error':
      return failureReason(message.errorCode).text;
  }
}

/**
 * O serviço de mensagens está fora do ar? Sim quando a mensagem de saída MAIS RECENTE do servidor
 * terminou em `error` com `max_retries` (as tentativas automáticas acabaram sem resposta). A nova
 * tentativa automática não publica evento, de propósito: o aviso some sozinho quando uma mensagem
 * de saída mais nova deixa de estar nesse estado. Mensagens de entrada não entram na conta.
 */
export function isProviderUnavailable(messages: readonly Message[]): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message?.direction !== 'out') continue;
    return message.status === 'error' && message.errorCode === 'max_retries';
  }
  return false;
}
